<template>
  <DeviceCheckFrame v-if="callState === 'check'" :participant="localParticipant" :devices="devices" theme="default"
    @cameraChange="onCameraChange" @microphoneChange="onMicrophoneChange" @speakerChange="onSpeakerChange"
    @joinCall="prejoinCall" @runNetworkCheck="runNetworkCheck" />

  <ClassroomEarly v-else-if="callState === 'early'" :theme="theme" :start-time-utc="startTimeUtc"
    :max-early-mins="maxEarlyMins" @joinCall="prejoinCall" />
  <ClassroomLate v-else-if="callState === 'late'" :theme="theme" :start-time-utc="startTimeUtc"
    :max-early-mins="maxEarlyMins" />
  <ClassroomWrongDevice v-else-if="callState === 'wrongDevice'" />
  <ClassroomError v-else-if="callState === 'error'" />

  <JoinFrame v-else-if="callState === 'prejoin' && localParticipant" :participant="localParticipant" :devices="devices"
    :theme="theme" :lesson-data="lessonData" @cameraChange="onCameraChange" @microphoneChange="onMicrophoneChange"
    @videoClick="onVideoClick" @muteClick="onParticipantMuteClick" @audioClick="onAudioClick" @joinCall="joinCall"
    @leaveClick="onLeaveClick" />

  <ClassroomLobby v-else-if="callState === 'lobby'" :theme="theme" :start-time-utc="startTimeUtc" />

  <CallFrame v-else-if="callState === 'active'" :participants="participants" :waiting-participants="waitingParticipants"
    :expected-participants="expectedParticipants" :theme="theme" :local-participant="localParticipant"
    :is-teacher="isTeacher" :enable-chat="classroomSettings.enableChat"
    :enable-screen-share="classroomSettings.enableScreenShare" :student-logins="studentLogins" :devices="devices"
    :messages="chatMessages" :screen-shares="screenShares" :screen-share-settings="screenShareSettings"
    :classroom-settings="classroomSettings" :lesson-data="lessonData" :quiz="quiz" @videoClick="onVideoClick"
    @audioClick="onAudioClick" @screenShareClick="onScreenShareClick" @cameraChange="onCameraChange"
    @microphoneChange="onMicrophoneChange" @speakerChange="onSpeakerChange"
    @participantHideClick="onParticipantHideClick" @participantMuteClick="onParticipantMuteClick"
    @sendMessage="sendChat" @leaveClick="onLeaveClick" @admitClick="onAdmitClick" @denyClick="onDenyClick"
    @emojiClick="onEmojiClick" @eject="onEjectClick" @handClick="onHandClick" @pinScreenShare="onPinScreenShare"
    @stopParticipantScreenShare="onStopParticipantScreenShare" @toggleChat="onToggleChat"
    @deleteChatMessage="onDeleteChatMessage" @dismissHand="onDismissHand" @toggleBackground="onToggleBackground"
    @upgrade-changed="onUpgradeChanged" @observation-changed="onObservationChanged" @toggleQuiz="toggleQuiz"
    @quizSubmit="onQuizSubmit" @quizHideClick="onQuizHideClick" />

  <CallEnded v-else-if="callState === 'ended' || callState === 'denied'" :denied="callState === 'denied'" :theme="theme"
    :lesson-uuid="lessonUuid" :student-uuid="studentUuid" :teacher-uuid="teacherUuid" :show-journey="showJourney"
    @join-click="prejoinCall" />

  <JoinLinkInvalid v-else-if="callState === 'invalid'" />
  <Disconnected v-else-if="callState === 'disconnected'" />

  <ClassroomWaiting v-else :theme="theme" />
  <ModalMessage ref="modalMessage" />
</template>

<script setup>
import env from '@/env.js';
import daily from '@daily-co/daily-js';
import apiHelpers from '@/scripts/helpers/apiHelpers.js';
import domHelpers from '@/scripts/helpers/domHelpers.js';
import logger from '@/scripts/logger.js';
import CallFrame from '@/components/classroom/CallFrame.vue';
import CallEnded from '@/components/classroom/CallEnded.vue';
import JoinLinkInvalid from '@/components/classroom/JoinLinkInvalid.vue';
import Disconnected from '@/components/classroom/Disconnected.vue';
import ClassroomLobby from '@/components/classroom/ClassroomLobby.vue';
import ClassroomEarly from '@/components/classroom/ClassroomEarly.vue';
import ClassroomWrongDevice from '@/components/classroom/ClassroomWrongDevice.vue';
import ClassroomError from '@/components/classroom/ClassroomError.vue';
import ClassroomLate from '@/components/classroom/ClassroomLate.vue';
import DeviceCheckFrame from '@/components/classroom/deviceCheck/DeviceCheckFrame.vue';
import JoinFrame from '@/components/classroom/JoinFrame.vue';
import ClassroomWaiting from '@/components/classroom/ClassroomWaiting';
import ModalMessage from '@/components/ModalMessage.vue';
import { ref, onMounted, onUnmounted, computed, provide, toRaw, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useCookies } from 'vue3-cookies';
import { CallStatus } from '@/logic/callStatus';
import _ from 'lodash';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import doorbellSound from "@/assets/sounds/doorbellSound.wav";
import { useSoundPlayer } from "@/composables/useSoundPlayer";
import { useThemeBackground } from "@/composables/useThemeBackground";
import { globalState } from '@/scripts/store';

const $toast = useToast();

var call,
  emojiTimeout,
  callStatus = new CallStatus(),
  sendQualities = ['low', 'medium', 'high'],
  defaultSendQuality = 2,
  defaultReceiveLayers = 2,
  callListeners = {
    'cpu-load-change': onCpuLoadChanged,
    'participant-joined': onParticipantJoined,
    'participant-updated': onParticipantUpdated,
    'participant-left': onParticipantLeft,
    'joined-meeting': onJoinedMeeting,
    'local-screen-share-stopped': onScreenShareEnded,
    'local-screen-share-started': onScreenShareStarted,
    'network-quality-change': onNetworkQualityChanged,
    error: onCallError,
    'nonfatal-error': onNonFatalError,
    'left-meeting': onLeftMeeting,
    // 'camera-error': onDeviceError,
    'app-message': processAppMessage,
    'local-audio-level': logMicrophoneLevel,
    'recording-error': handleRecordingError,
    'recording-started': onStartRecording,
    'track-stopped': onTrackStopped,
    'track-started': onTrackStarted,
    'waiting-participant-added': onWaitingParticipantsAdded,
    'waiting-participant-removed': onWaitingParticipantsRemoved,
  };

function onJoinedMeeting() {
  setTimeout(() => {
    updateCallPerformanceSettings();
  }, 1000);
}

let pingTimeout = null;

const roomUrl = ref(null),
  recording = ref(false),
  name = ref(null),
  lastInitial = ref(null),
  theme = ref(null),
  virtualBackgroundUrl = ref(null),
  startTimeUtc = ref(null),
  backgroundStatus = ref('off'),
  chatMessages = ref([]),
  participants = ref(null),
  waitingParticipants = ref(null),
  devices = ref(null),
  isTeacher = ref(false),
  studentLogins = ref(null),
  route = useRoute(),
  callState = ref('launch'),
  hasLesson = ref(false),
  showJourney = ref(false),
  lessonUuid = ref(null),
  studentUuid = ref(null),
  teacherUuid = ref(null),
  transcribe = ref(false),
  quiz = ref(null),
  localMicrophoneLevel = ref(0),
  { cookies } = useCookies(),
  classroomSettings = ref({ enableChat: true, enableScreenShare: true }),
  screenShareSettings = ref({ pinned: null, pinnedWidth: null }),
  devicesChecked = ref(false),
  modalMessage = ref(null),
  lessonData = ref(null),
  maxEarlyMins = ref(15), // How many minutes before lesson should student be able to access the lobby?
  maxLateMins = ref(null), // After how many minutes do we deny entry?
  expectedParticipants = ref(null),
  adminToken = ref(null);

// Initialize background composable
const { backgroundImage } = useThemeBackground('virtual', theme);

// Reactive gate: Apply background only when image AND token exist
watch([backgroundImage, adminToken, callState], ([newUrl, token]) => {
  if (newUrl && token) {
    virtualBackgroundUrl.value = newUrl;

    // Check call object exists and state is relevant before applying effects
    if (call && (callState.value === 'prejoin' || callState.value === 'active')) {
      addBackgroundEffects();
    }
  }

  if (call?.stopLocalAudioLevelObserver && callState.value !== 'check') {
    try {
      // Rejects asynchronously if the observer was never started, so catch the promise too
      call.stopLocalAudioLevelObserver()?.catch?.(() => { });
    }
    catch (error) {
      // Ignore
    }
  }
}, { immediate: true });

provide('localMicrophoneLevel', localMicrophoneLevel);
provide('hasLesson', hasLesson);
provide('isTeacher', isTeacher);

const doorbellPlayer = useSoundPlayer({
  sound: doorbellSound,
  loop: false,
  devices: devices
});

onMounted(async () => {
  lessonUuid.value = isTest() ? 'test' : route.params.lessonUuid;
  studentUuid.value = isTest('Student') ? 'test' : route.params.studentUuid;
  teacherUuid.value = route.params.teacherUuid;
  hasLesson.value = lessonUuid.value ? true : false;

  launchCall();

  // Listen to online/offline events
  window.addEventListener('offline', onOffline);
  window.addEventListener('online', onOnline);
});

onUnmounted(() => {
  if (!call) return;

  removeCallListeners();

  // Listen to online/offline events
  window.removeEventListener('offline', onOffline);
  window.removeEventListener('online', onOnline);
});

function isTest(type = '') {
  return route.name.indexOf(`ClassroomTest${type}`) === 0;
}

const screenShares = computed(() => {
  return getScreenShares();
});

const localParticipant = computed(() => {
  return getLocalParticipant();
});

function getLocalParticipant() {
  return _.find(participants.value, (p) => {
    return p.local;
  });
}

async function launchCall() {
  // If just a check
  if (!lessonUuid.value && !studentUuid.value) {
    runDeviceCheck();
  } else {
    prejoinCall();
  }
}

function onOnline() {
  setUserData('online', true);
}

function onOffline() {
  setUserData('online', false);
}

function needsDeviceCheck() {

  // We need a device check if one or more device lists are empty, or if we haven't had a recent device check
  return (
    !devicesChecked.value &&
    (!devices.value ||
      !devices.value.camera ||
      devices.value.camera.length === 0 ||
      !devices.value.microphone ||
      devices.value.microphone.length === 0 ||
      !getLastDeviceCheckResults())
  );
}

async function runDeviceCheck(theme) {
  if ((!theme || theme == 'free') && isMobileOrTablet()) {
    callState.value = 'wrongDevice';
  }
  else {
    await getDevices();
    if (!await createCall()) {
      return;
    }

    call.startLocalAudioLevelObserver(200);

    callState.value = 'check';
    devicesChecked.value = true;

    const deviceCheckResults = getLastDeviceCheckResults();
    recordLessonEvent('deviceCheck', { results: deviceCheckResults });
  }
}

async function onPinScreenShare(data) {
  // Pin if different, unpin if it's the same participant (toggle)
  screenShareSettings.value.pinned =
    screenShareSettings.value.pinned === data.participant.session_id
      ? null
      : data.participant.session_id;
  screenShareSettings.value.pinnedWidth = data.width;
  sendScreenshareSettings();

  await updateRecording();;
}

function onStopParticipantScreenShare(participant) {
  updateParticipant(participant, { setScreenShare: false });
}

function onToggleChat() {
  classroomSettings.value.enableChat = !classroomSettings.value.enableChat;
  sendClassroomSettings();
}

function sendScreenshareSettings(participant) {
  sendAppMessage(
    'screenShareSettings',
    toRaw(screenShareSettings.value),
    participant
  );
}

function sendClassroomSettings(participant) {
  sendAppMessage(
    'classroomSettings',
    toRaw(classroomSettings.value),
    participant
  );
}

function sendAppMessage(type, content, participant = null) {
  var data;

  try {
    if (hasAccessLevel('full')) {
      data = { type: type, content: content };
      call.sendAppMessage(data, participant ? participant.session_id : '*');

      recordLessonEvent('sendAppMessage', data);
    }
  } catch (err) {
    logger.error(err, JSON.stringify(data));
  }
}

function hasAccessLevel(level) {
  return call.accessState()?.access.level === level;
}

async function prejoinCall(params) {
  var callData,
    data,
    deviceCheckResults;

  callState.value = null;
  recording.value = false;

  callData = await fetchCallData();
  if (callData && !callData.error) {
    if (callData.redirect) {
      // Redirect to Zoom
      window.location.href = callData.redirect;
    } else {
      // Otherwise process all the call info and take them to the lobby
      hasLesson.value = true;
      lastInitial.value = callData.lastInitial;
      await getDevices();
      if (needsDeviceCheck()) {
        runDeviceCheck(callData.theme);
      } else {

        //adminToken.value = !isTest('Student') ? 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJwIjp7ImNhIjp0cnVlfSwiZCI6ImI2MTczYTNmLWJhNDEtNDJlOS1hNjVjLWI2YzZjNzgwZGUwNyIsImlhdCI6MTc3MzIzMjMxMH0.m9lnlPy6AwF46QblhZeBbX-a5dPXV46tRDZ3gDu8y-Q' : null;
        adminToken.value = callData.adminToken; // Triggers background gate

        theme.value = callData.theme;
        startTimeUtc.value = callData.startTimeUtc;
        showJourney.value = callData.showJourney ? true : false;

        globalState.value.observations = callData.observations;

        if ((theme.value == 'free' || (theme.value == 'launchpad' && callData.weekNumber < 3))
          && callData.admitted == 0) {
          maxLateMins.value = 10;
        }

        transcribe.value = callData.transcribe ? true : false;

        if (checkCanJoin(callData)) {
          if (!await createCall()) {
            return;
          }

          if (callData.lessonUuid) {
            lessonUuid.value = callData.lessonUuid;
          }

          if (lessonUuid.value) {
            name.value = callData.nickname || callData.name;
            roomUrl.value = callData.url;
            //roomUrl.value = 'https://cococoders.daily.co/IO5fYcGO5n9nqrvnXAET';//callData.url;
            studentLogins.value = callData.studentLogins;

            if (adminToken.value) {
              isTeacher.value = true;

              expectedParticipants.value = shapeExpectedParticipants(
                callData.expectedStudents
              );
            }

            lessonData.value = {
              time: callData.time,
              uuid: lessonUuid.value,
              date: callData.dateString,
              productName: callData.productName,
              productType: callData.productType,
              contentType: callData.contentType,
              weekNumber: callData.weekNumber,
              logins: {},
            };

            quiz.value = callData.quiz;

            await call.preAuth(getCallOptions());
            callState.value = 'prejoin';

            if (params && params.deviceCheckResults) {
              deviceCheckResults = params.deviceCheckResults;
            } else {
              deviceCheckResults = getLastDeviceCheckResults();
            }

            if (deviceCheckResults) {
              data = data || {};
              data.deviceCheckResults = deviceCheckResults;
            }

            recordLessonEvent('prejoined', data);
          } else {
            callState.value = 'invalid';
          }
        }
      }
    }
  } else if (
    callData?.error &&
    (callData.error.includes('Network Error') || callData.error.includes('timeout exceeded')
    )
  ) {
    callState.value = 'disconnected';
  } else {
    callState.value = 'invalid';
  }
}

function checkCanJoin(callData) {
  const alreadyJoined = callData.attended > 0;

  if (!adminToken.value) {

    // if (studentUuid.value == '26115d54-04a2-4b21-81f2-0fce805bc1f3') return true;

    if (!alreadyJoined && ['free', 'launchpad'].includes(theme.value) && isMobileOrTablet()) {
      callState.value = 'wrongDevice';
      return false;
    }
    // If student is too early, show the early screen
    else if (isEarly()) {
      callState.value = 'early';
      recordLessonEvent('early', { startTimeUtc: startTimeUtc.value });
      return false;
    }
    else if (!alreadyJoined && isTooLate()) {
      callState.value = 'late';
      return false;
    }
  }

  return true;
}

function isTooLate() {
  // 1. Ensure we have the values needed
  if (!startTimeUtc.value || !maxLateMins.value) return false;

  // 2. Calculate the difference in milliseconds
  // Date.now() is "now", startTimeUtc is "then"
  const diffInMs = Date.now() - Number(startTimeUtc.value);

  // 3. Convert milliseconds to minutes
  const minutesPastStart = diffInMs / (1000 * 60);

  // 4. Return true if they've exceeded the limit
  // (We use 0 as a floor so we don't return true for future lessons)
  return minutesPastStart > maxLateMins.value;
}

function isEarly() {
  if (route.query.override == '1') return false;

  // 1. Ensure we have the values
  if (!startTimeUtc.value || !maxEarlyMins.value) return false;

  // 2. Calculate time remaining until the lesson starts
  // startTimeUtc is "then", Date.now() is "now"
  const msUntilStart = Number(startTimeUtc.value) - Date.now();

  // 3. Convert that gap to minutes
  const minutesUntilStart = msUntilStart / (1000 * 60);

  // 4. If the gap is LARGER than the allowed join window, they are too early
  return minutesUntilStart > maxEarlyMins.value;
}

function shapeExpectedParticipants(students) {
  var result;

  if (students) {
    result = [];
    for (var student of students) {
      const userName = `${student.firstName} ${student.lastName}`;
      result.push({
        user_name: userName,
        userData: {
          studentUuid: student.uuid
        },
      });

      // Populate existing student observations for the lesson
      globalState.value.studentObservations[student.uuid || userName] = student.observations;
      globalState.value.studentUpgradeBadges[student.uuid || userName] = student.upgradeBadges;
    }
  }

  return result;
}

function getLastDeviceCheckResults() {
  var result;

  result = cookies.get('deviceCheck');

  return result;
}

async function startCamera() {
  await call.startCamera();

  setActiveInputDevices(await call.getInputDevices());
  checkDefaultDevices();
}

async function goToLobby() {
  var accessResponse;

  callState.value = 'lobby';

  accessResponse = await call.requestAccess({
    name: name.value,
    access: {
      level: 'full',
    },
  });

  if (accessResponse.granted) {
    for (var participant of participants.value) {
      if (!participant.local) {
        setTrackSubscription(participant, false, false);
      }
    }

    callState.value = 'active';

    // Delay 'admitted' lessonEvent, to make sure a 'join' event is sent first
    // Had some cases where they both hit the server at the same time
    setTimeout(() => {
      recordLessonEvent('admitted');
      sendPing();
    }, 1000);
  } else {
    onDeniedAccess();
  }
}

function sendPing() {
  pingTimeout = setTimeout(() => {
    recordLessonEvent('ping');
    sendPing();
  }, 120000);
}

function stopPing() {
  clearTimeout(pingTimeout);
}

function onDeniedAccess() {
  callState.value = 'denied';
  recordLessonEvent('denied');
}

async function joinCall() {
  doorbellPlayer.init();
  callState.value = null;

  try {
    await call.join(getCallOptions());
  }
  catch {
    // Daily also emits an 'error' event for this, which onCallError handles and logs
    return;
  }

  await setCallSettings();

  if (env.dev) {
    addFakeParticipants(2);
  }

  if (isTeacher.value) {
    callState.value = 'active';
  } else {
    goToLobby();
  }

  recordLessonEvent('joined');
}

async function createCall() {
  call = daily.getCallInstance();

  try {
    if (!call) {
      call = daily.createCallObject();

      if (env.dev) {
        window.dailyCall = call;
      }

      addCallListeners();
      await startCamera();
      setUserData();
    }

    refreshParticipants(true);
  }
  catch (err) {
    console.log(err)
    const errorMessage = err?.message || err?.toString() || '';

    const isWebRTCBlockedOrUnsupported =
      errorMessage.includes('WebRTC not supported or suppressed') ||
      errorMessage.includes('getGUMConstraints is not a function') ||
      // Catch generic missing RTCPeerConnection / getUserMedia API errors
      errorMessage.includes('RTCPeerConnection is not defined') ||
      errorMessage.includes('navigator.mediaDevices is undefined');

    if (isWebRTCBlockedOrUnsupported) {
      // Shows the error screen. Mostly bots or browsers with WebRTC disabled, so not logged
      callState.value = 'error';
      return null;
    } else {
      // Handle other unexpected errors
      logger.error(err);
    }
  }

  return call;
}

function addCallListeners() {
  for (var key in callListeners) {
    call.on(key, callListeners[key]);
  }

  callStatus.on('status-changed', handleCallStatusChange);
}

function removeCallListeners() {
  for (var key in callListeners) {
    call.off(key, callListeners[key]);
  }
}

function getCallOptions() {
  var result;

  result = {
    url: roomUrl.value,
    userName: name.value,
    sendSettings: { video: { maxQuality: sendQualities[defaultSendQuality] } },
    receiveSettings: { video: { layer: defaultReceiveLayers } }
  };

  if (adminToken.value) {
    result.token = adminToken.value;
  }

  return result;
}

async function setUserData(key, value) {
  var data, participant;

  // If no specific data, set the default user data values
  if (!key) {
    data = {
      online: true,
      lastInitial: lastInitial.value
    };


    if (teacherUuid.value) {
      data.teacherUuid = teacherUuid.value;
    }

    if (studentUuid.value) {
      data.studentUuid = studentUuid.value;
    }
  }
  // Otherwise, update with specific key/value
  else {
    participant = getLocalParticipant();
    if (participant) {
      data = { ...participant.userData };
      data[key] = value;
    }
  }

  if (data) {
    await updateUserData(data);
  }
}

async function removeUserData(key) {
  var data;

  data = getUserData();
  if (data) {
    data = _.clone(data);

    if (data[key]) {
      delete data[key];
    }

    await updateUserData(data);
  }
}

async function updateUserData(data) {
  var safeData;

  safeData = JSON.parse(JSON.stringify(data));
  await call.setUserData(safeData);
}

function getUserData() {
  return getLocalParticipant()?.userData;
}

async function addFakeParticipants(howMany) {
  for (var i = 0; i < howMany; i++) {
    call.addFakeParticipant();
  }
}

async function setCallSettings() {
  call.updateInputSettings({
    audio: {
      processor: {
        type: 'noise-cancellation',
      },
    },
  });

  call.setSubscribeToTracksAutomatically(false);
}

async function fetchCallData() {
  var endpoint;

  if (lessonUuid.value) {
    if (isTest('Student')) {
      endpoint = `classroom/test/${teacherUuid.value}`;
    } else if (isTest('Teacher')) {
      endpoint = `classroom/test/t/${teacherUuid.value}`;
    } else {
      // From /classroom link with lessonUuid student/teacher UUID
      endpoint = teacherUuid.value
        ? `classroom/t/${teacherUuid.value}/${lessonUuid.value}`
        : `classroom/${studentUuid.value}/${lessonUuid.value}`;
    }
  } else {
    // From /join link
    endpoint = `classroom/${studentUuid.value}`;
  }

  return await apiHelpers.get(endpoint, true);
}

function addBackgroundEffects() {
  if (virtualBackgroundUrl.value) {
    setBackgroundImage(virtualBackgroundUrl.value);
  } else {
    setBackgroundBlur(0.7);
  }

  backgroundStatus.value = 'on';
}

function removeBackgroundEffects() {
  if (backgroundStatus.value === 'on') {
    call.updateInputSettings({
      video: {
        processor: {
          type: 'none',
        },
      },
    });

    backgroundStatus.value = 'off';
  }
}

function toggleBackground() {
  if (backgroundStatus.value === 'on') {
    removeBackgroundEffects();
    backgroundStatus.value = 'disabled';
  } else {
    addBackgroundEffects();
  }

  recordLessonEvent('toggledBackground', { state: backgroundStatus.value });
}

function setBackgroundBlur(strength) {
  if (call) {
    call.updateInputSettings({
      video: {
        processor: {
          type: 'background-blur',
          config: { strength: strength },
        },
      },
    });
  }
}

function setBackgroundImage(url) {
  call.updateInputSettings({
    video: {
      processor: {
        type: 'background-image',
        config: {
          source: url,
        },
      },
    },
  });
}

function onParticipantUpdated() {
  var updated, existing, diff;

  updated = arguments[0].participant;
  refreshParticipants();

  // Find the updated participant in the participants array
  existing = _.find(participants.value, (p) => {
    return p.session_id === updated.session_id;
  });

  if (existing) {
    // Get list of properties that have changed
    diff = getObjectDiff(existing, updated);

    // Apply the updates to the participant
    for (var key of diff) {
      existing[key] = updated[key];
    }

    // If the pinned user has stopped screensharing, stop the pin
    if (
      isTeacher.value &&
      screenShareSettings.value.pinned == existing.session_id &&
      (!existing?.tracks?.screenVideo?.state ||
        existing.tracks.screenVideo.state === 'off')
    ) {
      screenShareSettings.value.pinned = null;
      sendScreenshareSettings();
    }
  }
}

function getObjectDiff(obj1, obj2) {
  const diff = Object.keys(obj1).reduce((result, key) => {
    if (!obj2.hasOwnProperty(key)) {
      result.push(key);
    } else if (_.isEqual(obj1[key], obj2[key])) {
      const resultKeyIndex = result.indexOf(key);
      result.splice(resultKeyIndex, 1);
    }
    return result;
  }, Object.keys(obj2));

  return diff;
}

function onParticipantJoined(action) {
  addParticipant(action.participant);
}

function addParticipant(participant) {
  var isPinned, screenShareVideoSubscription, screenShareAudioSubscription;

  if (isTeacher.value) {
    sendClassroomSettings(participant);
    sendScreenshareSettings(participant);
    fetchStudentLogins(participant);
  }

  // Subscribe (or not) to screen share for this new participant
  isPinned = participant.session_id === screenShareSettings.value.pinned;

  screenShareVideoSubscription = isPinned || isTeacher.value ? true : false;
  screenShareAudioSubscription = false; // isPinned ? true : isTeacher.value === true ? 'staged' : false;

  setTrackSubscription(
    participant,
    screenShareVideoSubscription,
    screenShareAudioSubscription
  );

  participants.value.push(participant);

  startRecording();
}

async function startRecording() {
  // Only record from the teacher's instance
  if (isTeacher.value) {
    // If we're already recording, update the settings
    if (recording.value) {
      await updateRecording();
    }
    // Otherwise start a new recording
    else {
      await call.startRecording(getRecordingParams());

      recordLessonEvent('startRecording');

      if (transcribe.value) {
        call.startTranscription();
      }

      recording.value = true;
    }
  }
}

async function updateRecording() {
  const recordingParams = getRecordingParams();
  await call.updateRecording(recordingParams);
}

function getRecordingParams() {
  const participantsString = ['*'];
  if (screenShareSettings.value?.pinned) {
    participantsString.unshift(screenShareSettings.value.pinned);
  }

  const sessionString = _(participants.value)
    .orderBy([item => !!item.local, item => item.user_name?.toLowerCase()], ['desc', 'asc'])
    .map('session_id')
    .join(',');

  const params = {
    type: 'cloud',
    backgroundColor: '#2a0066',
    layout: {
      preset: 'custom',
      participants: {
        video: participantsString,
        audio: participantsString
      },
      composition_params: {
        mode: 'grid',
        'videoSettings.preferredParticipantIds': sessionString,
        'videoSettings.preferScreenshare': true,
        'videoSettings.omitExtraScreenshares': true,
        'videoSettings.showParticipantLabels': true,
        'videoSettings.grid.useDominantForSharing': true,
        'videoSettings.placeholder.bgColor': '#1a003e',
        'videoSettings.highlight.color': '#ff004d'
      }
    }
  }

  return params;
}

async function stopRecording() {
  call.stopRecording();
  recording.value = false;
}

async function fetchStudentLogins(participant) {
  var endpoint, uuid, logins;

  uuid = participant.userData?.studentUuid;
  if (uuid) {
    endpoint = `logins/${uuid}`;
    logins = await apiHelpers.get(endpoint);

    if (logins && !logins.error) {
      lessonData.value.studentLogins = lessonData.value.studentLogins || {};
      lessonData.value.studentLogins[uuid] = logins;
    }
  }
}

function setTrackSubscription(
  participant,
  videoSubscription,
  audioSubscription
) {
  // Teacher is always subscribed to all
  if (!participant.local) {
    updateParticipant(participant, {
      setSubscribedTracks: {
        audio: true,
        video: true,
        screenVideo: videoSubscription,
        screenAudio: audioSubscription,
      },
    });
  }
}

function splitChatMessage(text) {
  if (!text) return [];

  const maxLength = 3000;
  const result = [];

  // Use a simple loop with slice to jump ahead by 3000 characters at a time
  for (let i = 0; i < text.length; i += maxLength) {
    result.push((text.slice(i, i + maxLength)).trim());
  }

  return result;
}

async function sendChat(text) {
  var content, id, parts;

  // Split text up if too long
  parts = splitChatMessage(text);

  for (var part of parts) {
    id = crypto && crypto?.randomUUID ? crypto.randomUUID() : Date.now();
    content = { text: part, name: name.value, id: id };
    sendAppMessage('sendChatMessage', content);
    content.name += ' (You)';
    addChat(content);
    await recordLessonEvent('chatted', { message: part });
  }
}

function onDeleteChatMessage(id) {
  sendAppMessage('deleteChatMessage', { id: id });
  deleteChatMessage(id);
}

function deleteChatMessage(id) {
  _.remove(chatMessages.value, (c) => {
    return c.id === id;
  });
  recordLessonEvent('deleteChatMessage', { id: id });
}

// Add chat message to local message array
function processAppMessage(message) {
  var data = message.data;
  switch (data.type) {
    case 'sendChatMessage':
      receiveChatMessage(data.content);
      break;
    case 'deleteChatMessage':
      deleteChatMessage(data.content.id);
      break;
    // case 'stopScreenShare':
    //   stopScreenShare();
    //   break;
    case 'screenShareSettings':
      updateScreenShareSettings(data.content);
      break;
    case 'classroomSettings':
      updateClassroomSettings(data.content);
      break;
    case 'dismissHand':
      dismissHand();
      break;
    case 'toggleBackground':
      toggleBackground();
      break;
    case 'startQuiz':
      startQuiz(data.content);
      break;
    case 'endQuiz':
      endQuiz();
      break;
  }
}

function updateScreenShareSettings(newSettings) {
  var oldPinned, pinnedParticipant, newPinned;

  oldPinned = screenShareSettings.value.pinned;
  newPinned = newSettings.pinned;

  // Unsubscribe from unpinned
  if (oldPinned !== newPinned) {
    if (oldPinned) {
      setTrackSubscription({ session_id: oldPinned }, false, false);
    }

    // Subscribe to pinned participant
    if (newPinned) {
      pinnedParticipant = getParticipantBySessionId(newPinned);
      if (pinnedParticipant) {
        setTrackSubscription(pinnedParticipant, true, true);
      }
    }

    setTimeout(() => {
      updateCallPerformanceSettings(callStatus.status.mode);
    }, 1000);
  }

  screenShareSettings.value = newSettings;

  recordLessonEvent('updateScreenShareSettings', newSettings);
}

function getParticipantBySessionId(sessionId) {
  return _.find(participants.value, (p) => {
    return p.session_id === sessionId;
  });
}

function updateClassroomSettings(newSettings) {
  classroomSettings.value = newSettings;
  if (!classroomSettings.value.enableScreenShare) {
    stopScreenShare();
  }
  recordLessonEvent('updateClassroomSettings', newSettings);
}

function receiveChatMessage(content) {
  addChat(content);
  recordLessonEvent('receiveChatMessage', content);
}

function addChat(content) {
  var text;

  text = domHelpers
    .embedLinks(domHelpers.htmlEncode(content.text))
    .replace(/(?:\r\n|\r|\n)/g, '<br>');
  chatMessages.value.push({
    content: text,
    name: content.name,
    id: content.id,
  });
}

function updateParticipant(participant, settings) {
  var sessionId = participant.session_id;
  call.updateParticipant(sessionId, settings);
}

function getScreenShares(localOnly = false) {
  var shares;

  if (participants.value) {
    shares = participants.value.filter(
      (p) => p.screenVideoTrack && (!localOnly || p.local)
    );
  }

  return shares && shares.length > 0 ? shares : null;
}

function isScreensharing() {
  return getScreenShares(true) ? true : false;
}

async function leaveCall(reason) {
  if (call) {
    recordLessonEvent('left', { reason: reason });

    if (isScreensharing()) {
      await stopScreenShare();
    }

    call.leave().then(() => {
      call.destroy();
    });
  }
  callState.value = 'ended';
  stopPing();
  stopRecording();
  participants.value = null;
}

function logMicrophoneLevel(level) {
  localMicrophoneLevel.value = level.audioLevel;
}

async function requestUserPermissions() {
  const results = { audio: null, video: null };

  // Test Audio
  try {
    await navigator.mediaDevices.getUserMedia({ audio: true });
    results.audio = 'granted';
  } catch (e) {
    results.audio = e.name; // e.g., "NotFoundError"
  }

  // Test Video
  try {
    await navigator.mediaDevices.getUserMedia({ video: true });
    results.video = 'granted';
  } catch (e) {
    results.video = e.name; // e.g., "NotAllowedError"
  }

  recordLessonEvent('device-permission-report', results);
}

// Get list of available devices to show in UI
async function getDevices() {
  var availableDevices, cameras, microphones, speakers;

  try {
    // Create lists of available device types
    availableDevices = navigator.mediaDevices?.enumerateDevices
      ? await navigator.mediaDevices.enumerateDevices()
      : null;
    if (!availableDevices || !availableDevices[0]?.label) {
      await requestUserPermissions();
      availableDevices = await navigator.mediaDevices.enumerateDevices();
    }

    cameras = _.filter(availableDevices, (d) => {
      return d.kind == 'videoinput' && d.deviceId !== '' && d.label !== '';
    });
    microphones = _.filter(availableDevices, (d) => {
      return d.kind == 'audioinput' && d.deviceId !== '' && d.label !== '';
    });
    speakers = _.filter(availableDevices, (d) => {
      return d.kind == 'audiooutput' && d.deviceId !== '' && d.label !== '';
    });
  } catch (error) {
    // Ignore - device not granting permissions
  }

  // Set to object for rendering
  devices.value = {
    camera: cameras,
    microphone: microphones,
    speaker: speakers,
  };
}

// Look for previous device selections in localStorage
function checkDefaultDevices() {
  checkDefaultDevice('camera');
  checkDefaultDevice('microphone');
  checkDefaultDevice('speaker');
}

// Look for local storage value to override active device selection
function checkDefaultDevice(key) {
  var deviceId = localStorage[key];

  if (deviceId) {
    setActiveDevice(key, deviceId, true);
  }
}

function setActiveInputDevices(inputDevices) {
  setActiveDevice('camera', inputDevices.camera.deviceId);
  setActiveDevice('microphone', inputDevices.mic.deviceId);
  setActiveDevice('speaker', inputDevices.speaker.deviceId);
}

function setActiveDevice(key, deviceId, updateSystem = false) {
  var device = getDevice(key, deviceId),
    newDevices = _.clone(devices.value); // Needed for reactivity

  if (device && !device.active) {
    newDevices[key].forEach((d) => {
      d.active = d.deviceId === deviceId;
    });
    _.orderBy(newDevices[key], [
      function (d) {
        return !d.active ? true : false;
      },
    ]);
    devices.value = newDevices;
    if (updateSystem) {
      onDeviceChange(key, deviceId);
    }
  }

  return false;
}

function getDevice(key, deviceId) {
  return _.find(devices.value[key], (d) => {
    return d.deviceId === deviceId;
  });
}

async function recordLessonEvent(type, params = {}) {
  var data;
  params.device = navigator.userAgent;

  try {
    if (!isTest()) {
      data = {
        type: type,
        lessonUuid: lessonUuid.value,
        teacherUuid: params.teacherUuid || teacherUuid.value,
        studentUuid: params.studentUuid || studentUuid.value,
        data: params,
        sessionId: params.sessionId || getLocalParticipant()?.session_id,
      };

      if (env.dev) {
        console.log(`Lesson event: '${type}'`, data);
      }

      await apiHelpers.post(`sync/lessonEvent`, data);
    }
  } catch (error) {
    logger.error(
      error,
      `Couldn't record lesson event ${type} with params: ${JSON.stringify(
        params
      )}`
    );
  }
}

// EVENT HANDLERS

async function onScreenShareEnded() {
  await stopScreenShare();
  setTimeout(() => {
    updateCallPerformanceSettings(callStatus.status.mode);
  }, 1000);
}

async function onScreenShareStarted() {
  setTimeout(() => {
    updateCallPerformanceSettings(callStatus.status.mode);
  }, 1000);
}

async function onCameraChange(deviceId) {
  setActiveDevice('camera', deviceId, true);
}

async function onMicrophoneChange(deviceId) {
  setActiveDevice('microphone', deviceId, true);
}

async function onSpeakerChange(deviceId) {
  setActiveDevice('speaker', deviceId, true);
}

// Replace your existing onDeviceChange with this version
async function onDeviceChange(key, deviceId) {
  let params, isInput, newDevices;

  localStorage[key] = deviceId;

  switch (key) {
    case 'camera':
      params = { videoDeviceId: deviceId };
      isInput = true;
      break;
    case 'microphone':
      params = { audioDeviceId: deviceId };
      isInput = true;
      break;
    case 'speaker':
      params = { outputDeviceId: deviceId };
      isInput = false;
      break;
  }

  try {
    newDevices = isInput
      ? await call.setInputDevicesAsync(params)
      : await call.setOutputDeviceAsync(params);

    // Keep the UI in sync
    setActiveInputDevices(newDevices);
  } catch (err) {
    // When switching speakers, browsers may throw if there's no user gesture
    const name = err && (err.name || err.constructor?.name);
    const needsGesture = name === 'NotAllowedError' || name === 'SecurityError';

    if (!isInput && needsGesture) {
      // Log & fall back to default routing
      try {
        // Try to explicitly route to the default output device
        newDevices = await call.setOutputDeviceAsync({ outputDeviceId: 'default' });
        setActiveInputDevices(newDevices);
      } catch (fallbackErr) {
        // Even default might be blocked without a gesture (Safari/older Chrome flows)
        // In that case, do nothing: the <audio> element will already use the system default.

        logger.error(err, `Speaker change blocked; using system default output for student: '${studentUuid.value}' in lesson '${lessonUuid.value}'`);
        return;
      }

      // If it wasn't the speaker or a gesture-related issue, rethrow so you notice real errors
      throw err;
    }
  }
}

// Toggle local microphone in use (on/off)
function onAudioClick() {
  const audioOn = call.localAudio();
  call.setLocalAudio(!audioOn);
  recordLessonEvent(audioOn ? 'mic on' : 'mic off');
}

// Toggle local camera in use (on/off)
function onVideoClick() {
  const videoOn = call.localVideo();
  call.setLocalVideo(!videoOn);
  recordLessonEvent(videoOn ? 'camera on' : 'camera off');
}

function onParticipantMuteClick(participant) {
  if (participant.local || participant.audio) {
    updateParticipant(participant, { setAudio: !participant.audio });
  } else {
    modalMessage.value.show({
      message: `For privacy reasons, you can't unmute a user`,
      buttons: [{ label: 'OK' }],
    });
  }
}

function onParticipantHideClick(participant) {
  if (participant.video) {
    updateParticipant(participant, { setVideo: false });
  } else {
    modalMessage.value.show({
      message: `For privacy reasons, you can't turn on a user's camera`,
      buttons: [{ label: 'OK' }],
    });
  }
}

function onAdmitClick(participant) {
  call.updateWaitingParticipant(participant.id, { grantRequestedAccess: true });
}

function onDenyClick(participant) {
  call.updateWaitingParticipant(participant.id, {
    grantRequestedAccess: false,
  });
}

async function onScreenShareClick() {
  if (isScreensharing()) {
    stopScreenShare();
  } else {
    await startScreenShare();
  }
}

function onParticipantLeft(params) {
  var data;

  // Remove the participant from the participants array
  _.remove(
    participants.value,
    (p) => p.session_id === params.participant.session_id
  );

  checkTeacherIsPresent();

  data = {
    sessionId: params.participant.session_id,
    studentUuid: params.participant.userData.studentUuid,
    teacherUuid: params.participant.userData.teacherUuid,
  };
  recordLessonEvent('left', data);
}

function checkTeacherIsPresent() {
  var teachers;

  teachers = getTeachers();

  if (!teachers || teachers.length === 0) {
    leaveCall('noTeacher');
  }
}

function getTeachers() {
  return _.find(call.participants(), (p) => {
    return p.permissions?.canAdmin ? true : false;
  });
}

function onWaitingParticipantsAdded(data) {
  updateWaitingParticipants();
  const studentName = data?.participant?.name || 'A student';

  $toast.warning(`${studentName} is waiting to join`, {
    duration: 5000,
    dismissable: true,
  });

  doorbellPlayer.start();
}

function onWaitingParticipantsRemoved() {
  updateWaitingParticipants();
}

function updateWaitingParticipants() {
  var newWaiting = Object.values(call.waitingParticipants());

  newWaiting.forEach((p) => {
    p.isWaiting = true;
    p.user_name = p.name;
  });

  waitingParticipants.value = newWaiting;
}

function onLeaveClick() {
  leaveCall('left');
}

function onEmojiClick(key) {
  setUserData('emoji', key);

  if (emojiTimeout) {
    clearTimeout(emojiTimeout);
  }

  emojiTimeout = setTimeout(() => {
    removeUserData('emoji');
  }, 5000);

  recordLessonEvent('emoji', { type: key });
}

function onEjectClick(participant) {
  updateParticipant(participant, { eject: true });
}

function onHandClick() {
  var data;

  data = getUserData();

  if (data?.hand === true) {
    dismissHand();
  } else {
    raiseHand();
  }
}

function dismissHand() {
  setUserData('hand', false);
  recordLessonEvent('dismissHand');
}

function raiseHand() {
  setUserData('hand', true);
}

function onDismissHand(participant) {
  if (participant.local) {
    dismissHand();
  } else {
    sendAppMessage('dismissHand', null, participant);
  }
}

function onToggleBackground(participant) {
  if (participant.local) {
    toggleBackground();
  } else {
    sendAppMessage('toggleBackground', null, participant);
  }
}

function onCpuLoadChanged(data) {
  callStatus.handleStatusEvent('cpu', data.cpuLoadState);
}

function onNetworkQualityChanged(data) {
  var ourThreshold;

  // Use both Daily's threshold and quality number to determine our own level
  if (data.threshold === 'very-low' || data.quality < 40) {
    ourThreshold = 'very-low';
  } else if (data.threshold === 'low' || data.quality < 60) {
    ourThreshold = 'low';
  } else {
    ourThreshold = 'good';
  }

  callStatus.handleStatusEvent('network', ourThreshold);
}

async function updateSendSettings(settings) {
  await call.updateSendSettings(settings);
}

async function updateReceiveSettings(settings) {
  // If in a call
  if (['active', 'lobby'].includes(callState.value) && hasAccessLevel('full')) {
    call.updateReceiveSettings(settings);
  }
}

function handleCallStatusChange(data) {
  updateCallPerformanceSettings(data.mode);
}

async function updateCallPerformanceSettings(mode) {
  var presets, sendQuality, receiveLayers;

  switch (mode) {
    case 'bad':
      //presets = { video: 'bandwidth-optimized', screenVideo: 'detail-optimized' };
      sendQuality = defaultSendQuality - 2;
      receiveLayers = defaultReceiveLayers - 2;
      break;
    case 'ok':
      sendQuality = defaultSendQuality - 1;
      receiveLayers = defaultReceiveLayers - 1;
      break;
    case 'good':
      sendQuality = defaultSendQuality;
      receiveLayers = defaultReceiveLayers;
    default:
      presets = {
        video: 'bandwidth-optimized',
        screenVideo: 'detail-optimized',
      };
      //  presets = { video: 'default-video', screenVideo: 'motion-and-detail-balanced' };
      sendQuality = defaultSendQuality;
      receiveLayers = defaultReceiveLayers;
      break;
  }

  // Reduce send quality when also sending a screenshare video
  if (isScreensharing()) {
    sendQuality--;
  }

  if (!isTeacher.value) {
    if (screenShareSettings.value.pinned) {
      receiveLayers--;
    }
  }

  // // Reduce the receive quality for the teacher
  // // as they will have all of the screenshares coming in too
  // if (isTeacher.value === true) {
  //   receiveLayers--;
  // }

  // Ensure quality/layers aren't below zero
  receiveLayers = Math.max(0, receiveLayers);
  sendQuality = Math.max(0, sendQuality);

  if (presets) {
    await updateSendSettings(presets);
  }

  await updateSendSettings({
    video: { maxQuality: sendQualities[sendQuality] },
  });
  await updateReceiveSettings({ base: { video: { layer: receiveLayers } } });

  // Remove blur if CPU is high (students only)
  if (isTeacher.value === false && backgroundStatus.value !== 'disabled') {
    if (callStatus.status.cpu === 'high') {
      removeBackgroundEffects();
    }
  }

  setUserData('status', callStatus.status);
  recordLessonEvent('call-status', {
    mode: mode || 'default',
    status: callStatus.status,
  });
}

function refreshParticipants(force) {
  var keys, callParticipants;

  if (call) {
    if (force || !participants.value) {
      participants.value = [];
      callParticipants = call.participants();
      keys = Object.keys(callParticipants);

      // callParticipants has sessionId as key
      // Convert that into a standard array for our purposes
      for (var key of keys) {
        participants.value.push(callParticipants[key]);
      }
    }
  }
}

async function startScreenShare() {
  call.startScreenShare();
}

async function stopScreenShare() {
  if (hasAccessLevel('full')) {
    await call.stopScreenShare();
  }
}

function onStartRecording() {
  //console.log('Recording started');
}

function onTrackStopped(data) {
  logTrackChange('trackStopped', data);
}

function onTrackStarted(data) {
  logTrackChange('trackStarted', data);
}

function logTrackChange(label, data) {
  var params;

  if (!data.participant.local) {
    params = {
      participant: data.participant.user_name,
      paticipantSession: data.participant.session_id,
      type: data.type
    };

    recordLessonEvent(label, params);
  }
}

function handleRecordingError(error) {
  logger.error(
    error?.errorMsg,
    `Couldn't start recording for lesson UUID ${lessonUuid.value}`
  );
}

function onLeftMeeting() {
  if (!['ended', 'denied'].includes(callState.value)) {
    leaveCall('ejected');
  }
}

function onNonFatalError(error) {
  switch (error.type) {
    case 'screen-share-error':
      handleScreenshareError(error);
      break;
  }

  recordLessonEvent('non-fatal-error', error);
}

function handleScreenshareError(error) {
  var message = `Your device is preventing screen sharing in the classroom.<br><br>Please refer to your browser or operating system settings to enable screen sharing.`;

  if (error?.details?.category === 'permissions') {
    switch (error.details?.blockedBy) {
      case 'browser':
        message = `Your web browser is preventing screen sharing in the classroom.<br><br>Please refer to your browser's settings to enable screen sharing.`;
        break;
      case 'os':
        message = `Your operating system is preventing screen sharing in the classroom.<br><br>Please refer to your operating system settings to enable screen sharing.`;
        break;
      default:
        message = `Your device is preventing screen sharing in the classroom.<br><br>Please refer to your browser or operating system settings to enable screen sharing.`;
        break;
    }
  }

  modalMessage.value.show({
    title: `Can't start screen sharing`,
    message: message,
    buttons: [{ label: 'OK' }],
  });
}

function onCallError(error) {
  var userType, uuid;

  if (error.errorMsg == 'Join request rejected') {
    onDeniedAccess();
  } else {
    if (studentUuid.value) {
      userType = 'student';
      uuid = studentUuid.value;
    } else {
      userType = 'teacher';
      uuid = teacherUuid.value;
    }

    // Expected errors (e.g. expired rooms) are filtered in logger.js
    logger.error(
      error.errorMsg,
      `Problem with call for ${userType} ${uuid} in lesson ${lessonUuid.value}. URL: ${roomUrl.value}`
    );
  }
}

async function runNetworkCheck(callback) {
  var videoTrack, testResults;
  try {
    videoTrack = getLocalParticipant().tracks.video.persistentTrack;
    testResults = await call.testPeerToPeerCallQuality({
      videoTrack: videoTrack,
      duration: 10,
    });

    callback(testResults);
  } catch (error) {
    callback({ result: 'failed' });
  }
}

function onUpgradeChanged(data) {
  var params;

  params = {
    type: data.type,
    achieved: data.achieved,
    studentUuid: data.participant.userData?.studentUuid,
  };

  recordLessonEvent('upgradeChanged', params);
}

function onObservationChanged(data) {
  const studentUuid = data.participant.userData?.studentUuid || data.participant.user_name;
  const shortname = data.observation.shortname;

  globalState.value.studentObservations[studentUuid] ??= {};
  const count = globalState.value.studentObservations[studentUuid][shortname] = globalState.value.studentObservations[studentUuid][shortname] === 1 ? 0 : 1;

  // Prepare and record the event payload
  const params = {
    observationShortname: shortname,
    count,
    studentUuid: studentUuid,
    lessonUuid: lessonUuid.value
  };

  recordLessonEvent('observationChanged', params);
}

// Start quiz for student
function startQuiz(filloutId) {
  quiz.value = { filloutId: filloutId, running: true };
}

// End quiz for student
function endQuiz() {
  quiz.value = null;
}

// Toggle quiz (teacher control)
function toggleQuiz() {
  quiz.value.running = !quiz.value.running;
  if (quiz.value.running) {
    sendAppMessage('startQuiz', quiz.value.filloutId);
    recordLessonEvent('startQuiz');

    if (quiz.value.pollTimeout) {
      clearTimeout(quiz.value.pollTimeout);
    }

    if (!isTest()) {
      pollQuizResponses();
    }
  }
  else {
    sendAppMessage('endQuiz');
    recordLessonEvent('endQuiz');
  }
}

async function pollQuizResponses(expire = null) {
  const now = new Date();

  // If quiz has just stopped running, keep polling for 1 min, to catch straggler responses
  if (!quiz.value.running && !expire) {
    expire = new Date(now.getTime() + 1 * 60 * 1000);
  }

  // If quiz is still running, or poll expiry hasn't run out
  // fetch latest responses
  if (quiz.value.running || (expire && expire > now)) {
    await fetchQuizResponses();
    quiz.value.pollTimeout = setTimeout(() => {
      pollQuizResponses(expire).catch(console.error);
    }, 5000);
  }
}

async function fetchQuizResponses() {
  var responses;

  if (quiz.value) {
    responses = await apiHelpers.get(`quiz/responses?filloutId=${quiz.value.filloutId}&lesson=${lessonUuid.value}`);

    for (var i = 0; i <= 4; i++) {
      if (quiz.value.questions[i]) {
        quiz.value.questions[i].responses = responses[i];
      }
    }
  }
}

function onQuizSubmit(filloutId) {
  recordLessonEvent('quizSubmitted', { filloutId: filloutId });
}

function onQuizHideClick() {
  quiz.value = null;
  recordLessonEvent('quizHide');
}

//   // 1. Try User Agent Client Hints
//   if (navigator.userAgentData) {
//     const data = await navigator.userAgentData.getHighEntropyValues(["mobile"]);
//     if (typeof data.mobile === "boolean") return data.mobile;
//   }

//   // 2. Capability-based fallback
//   const coarse = matchMedia("(pointer: coarse)").matches;
//   const smallScreen = window.innerWidth < 900;
//   if (coarse && smallScreen) return true;

//   // 3. User Agent fallback (last resort)
//   const ua = navigator.userAgent.toLowerCase();
//   return /android|iphone|ipad|ipod|mobile|tablet/.test(ua);
// };
function isMobileOrTablet() {
  // 1. User Agent Client Hints (Highest Priority/Reliability)
  if (navigator.userAgentData && typeof navigator.userAgentData.mobile === "boolean") {
    if (navigator.userAgentData.mobile) {
      recordInvalidDevice('navigator.userAgentData.mobile == true');
      return true;
    }
    // If explicitly false, continue with heuristics
  }

  const ua = (navigator.userAgent || "").toLowerCase();

  // Critical iPadOS check (UA is 'macintosh' but has multiple touch points)
  const isIPadOSDesktop =
    /macintosh/.test(ua) &&
    navigator.maxTouchPoints &&
    navigator.maxTouchPoints > 1;

  // Touch and Pointer Capabilities
  const hasTouch =
    ("ontouchstart" in window) ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);

  const hasCoarsePointer =
    typeof matchMedia === "function" &&
    (matchMedia("(pointer: coarse)").matches ||
      matchMedia("(any-pointer: coarse)").matches);

  // Basic UA string match for mobile/tablet keywords
  const isMobileUA = /(android|iphone|ipad|ipod|mobile|tablet)/.test(ua);

  // Screen size check (e.g., min dimension < 900px)
  const minDimension = Math.min(screen.width, screen.height);
  const isSmallScreen = minDimension <= 700;

  // Final Decision
  if (
    (hasTouch || hasCoarsePointer) &&
    (isSmallScreen || isMobileUA || isIPadOSDesktop)
  ) {
    recordInvalidDevice('Final decision');
    return true; // Phone/Tablet/iPadOS
  }

  return false; // Treat everything else as desktop/laptop
}

function recordInvalidDevice(reason) {
  const { userAgentData, userAgent, maxTouchPoints } = navigator;
  recordLessonEvent('invalidDevice', {
    reason,
    navigator: { userAgentData, userAgent, maxTouchPoints },
    screen: { width: screen.width, height: screen.height },
    window: { width: window.innerWidth, height: window.innerHeight }
  });
}
</script>